import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, Loading, Meter, PageHeader, Panel, PanelBody } from '../ui/index.js';
import { fetchJson } from '../api.js';

/**
 * Carrier portal report (Jetpack & co): the ONLY page a carrier-scoped user
 * can reach (backend refuses everything else). Read-only — their parcels,
 * what the carrier recorded, what ghosted, amounts at risk.
 */

interface CarrierTotals {
  readonly total: number;
  readonly recorded: number;
  readonly ghosted: number;
  readonly ghostedAmount: number;
  readonly delivered: number;
  readonly paidPerMiddleware: number;
}

interface CarrierColis {
  readonly codeBarre: string;
  readonly dateAdd: string | null;
  readonly prix: number;
  readonly nom: string | null;
  readonly ville: string | null;
  readonly etatLabel: string;
  readonly paye: boolean;
  readonly recorded: boolean;
}

interface CarrierReport {
  readonly carrier: string;
  readonly totals: CarrierTotals;
  readonly page: number;
  readonly totalList: number;
  readonly colis: readonly CarrierColis[];
}

function money(value: number): string {
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function CarrierReportPage(): React.ReactElement {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const reportQuery = useQuery({
    queryKey: ['carrier-report', page, query],
    queryFn: () =>
      fetchJson<CarrierReport>(
        `/api/v1/shipping/carrier-report?page=${page}&q=${encodeURIComponent(query)}`,
      ),
  });

  const report = reportQuery.data;
  const totalPages =
    report === undefined ? 1 : Math.max(1, Math.ceil(report.totalList / 50));

  return (
    <div>
      <PageHeader
        index="TR"
        title={`Réconciliation Colis — ${report?.carrier ?? '…'}`}
        purpose="Vue lecture seule de vos colis enregistrés dans notre système : état de chaque colis et signalement des colis fantômes (envoyés mais absents de notre enregistrement). Aucune action n'est possible depuis cette page."
        meta={[
          { label: 'Colis', value: report === undefined ? '—' : report.totals.total.toLocaleString('fr-FR') },
          { label: 'Page', value: `${page} / ${totalPages}` },
        ]}
      />

      {reportQuery.isError && (
        <Panel className="mb-4">
          <PanelBody>
            <p className="text-sm text-ink">Rapport indisponible : {String(reportQuery.error.message)}</p>
          </PanelBody>
        </Panel>
      )}
      {reportQuery.isPending && <Loading label="Lecture du rapport…" />}

      {report !== undefined && (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
            <StatCard label="Colis au total" value={report.totals.total.toLocaleString('fr-FR')} tone="neutral" />
            <StatCard label="Enregistrés (chez nous)" value={report.totals.recorded.toLocaleString('fr-FR')} tone="good" />
            <StatCard label="Colis fantômes" value={report.totals.ghosted.toLocaleString('fr-FR')} tone="alarm" />
            <StatCard label="Montant à risque" value={money(report.totals.ghostedAmount) + ' DT'} tone="alarm" />
            <StatCard label="Livrés / payés (interne)" value={`${report.totals.delivered.toLocaleString('fr-FR')} / ${report.totals.paidPerMiddleware.toLocaleString('fr-FR')}`} tone="neutral" />
          </div>

          <Panel className="mb-4">
            <PanelBody>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-ink-soft">
                  {report.totals.recorded.toLocaleString('fr-FR')} colis enregistrés sur{' '}
                  {report.totals.total.toLocaleString('fr-FR')}
                </span>
                {report.totals.ghosted > 0 && (
                  <Badge tone="alarm">
                    {report.totals.ghosted.toLocaleString('fr-FR')} fantômes — réclamation en cours
                  </Badge>
                )}
              </div>
              <Meter
                value={report.totals.total === 0 ? 0 : report.totals.recorded / report.totals.total}
                tone={report.totals.ghosted === 0 ? 'good' : 'alarm'}
                className="mt-2"
              />
            </PanelBody>
          </Panel>

          <Panel>
            <PanelBody>
              <form
                className="mb-3 flex items-center gap-2"
                onSubmit={(event) => {
                  event.preventDefault();
                  setPage(1);
                  setQuery(search.trim());
                }}
              >
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Code-barres, client, ville…"
                  className="h-8.5 w-64 rounded-lg border border-border bg-card px-3 text-xs sm:text-sm text-ink placeholder:text-ink-ghost focus:border-herb-600 outline-none"
                />
                <button
                  type="submit"
                  className="h-8.5 rounded-lg border border-border bg-card px-3 text-xs font-medium text-ink hover:bg-cream"
                >
                  Chercher
                </button>
              </form>

              <div className="space-y-1.5">
                {report.colis.map((c) => (
                  <div
                    key={c.codeBarre}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border bg-cream/40 px-3 py-2 text-xs"
                  >
                    <span className="font-mono font-bold">{c.codeBarre}</span>
                    <Badge tone={c.recorded ? 'good' : 'alarm'}>
                      {c.recorded ? 'enregistré' : 'FANTÔME — non enregistré'}
                    </Badge>
                    <span className="min-w-0 flex-1 truncate text-ink-soft">
                      {c.nom ?? '—'}
                      {c.ville !== null && <span className="ml-1.5 text-ink-faint">· {c.ville}</span>}
                    </span>
                    <span className="font-mono text-ink">{money(c.prix)} DT</span>
                    <span className="text-ink-faint">{c.etatLabel}</span>
                    {c.paye && <Badge tone="signal">payé</Badge>}
                    <span className="font-mono text-[10px] text-ink-ghost">
                      {c.dateAdd === null ? '—' : new Date(c.dateAdd).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-3 flex items-center justify-between">
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-lg border border-border bg-card px-3 py-1 text-xs font-medium text-ink disabled:opacity-40"
                  >
                    ← Précédente
                  </button>
                  <span className="font-mono text-xs text-ink-faint">
                    Page {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded-lg border border-border bg-card px-3 py-1 text-xs font-medium text-ink disabled:opacity-40"
                  >
                    Suivante →
                  </button>
                </div>
              )}
            </PanelBody>
          </Panel>
        </>
      )}
    </div>
  );
}

function StatCard(props: {
  readonly label: string;
  readonly value: string;
  readonly tone: 'good' | 'alarm' | 'neutral';
}): React.ReactElement {
  return (
    <div className="rounded-xl border border-border bg-card p-3.5 shadow-xs">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-ink-faint">{props.label}</p>
      <p
        className={
          props.tone === 'alarm'
            ? 'mt-1 font-display text-xl font-semibold text-rust-700'
            : props.tone === 'good'
              ? 'mt-1 font-display text-xl font-semibold text-herb-700'
              : 'mt-1 font-display text-xl font-semibold text-ink'
        }
      >
        {props.value}
      </p>
    </div>
  );
}
